import React, { useMemo, useState } from "react";
import axios from "axios";
import { Form, Row, Col } from "react-bootstrap";
import { MdExpandMore, MdPictureAsPdf, MdSearch } from "react-icons/md";
import Sidebar from "../../../components/layout/Sidebar";
import DashboardHeader from "../../../components/layout/DashboardHeader";
import { BASE_URL } from "../../../env";
import { generateLoanSummaryForMember } from "../../../utils/utils";
import { buildAccountStatement, formatInr } from "../../../utils/accountStatement";
import { downloadAccountStatementPdf } from "../../../utils/accountStatementPdf";
import logo from "../../../assets/vetrivikaslogo.jpeg";
import "./AccountStatement.css";

const STATUS_TONE = { Clear: "good", Completed: "good", Foreclosed: "neutral", Pending: "warn" };
const GROUP_TITLES = { charges: "Loan amount and charges", repayments: "Repayments" };
const COLUMN_COUNT = 8;
const PRODUCT_BY_LOAN_TYPE = { "Business Loan": "BL", "GP Loan": "GPL" };

const money = (value) => (value === null || value === undefined || value === "" ? "-" : formatInr(value));

const StatementDocument = ({ statement }) => {
  const tone = STATUS_TONE[statement.status] || "neutral";
  let lastGroup = null;

  return (
    <article className="las-doc">
      <header className="las-head">
        <img src={logo} alt="Vetrivikas Capital" className="las-logo" />
        <div className="las-head-meta">
          <div className="las-title-row">
            <h2 className="las-title">Account Statement</h2>
            <span className="las-chip">{statement.productLabel}</span>
            <span className={`las-status las-status-${tone}`}>{statement.status}</span>
          </div>
          <div className="las-sub">
            Agreement No. <strong>{statement.agreementNo}</strong>
            <span className="las-dot" aria-hidden="true">·</span>
            Generated on {statement.generatedOn}
          </div>
        </div>
      </header>

      <div className="las-body">
        <section className="las-kpis" aria-label="Totals">
          <div className="las-kpi">
            <span className="las-kpi-label">Total Overall Debt</span>
            <span className="las-kpi-value">₹{formatInr(statement.totals.debt)}</span>
          </div>
          <div className="las-kpi">
            <span className="las-kpi-label">Total Paid</span>
            <span className="las-kpi-value">₹{formatInr(statement.totals.paid)}</span>
          </div>
          <div className="las-kpi las-kpi-accent">
            <span className="las-kpi-label">Remaining To Pay</span>
            <span className="las-kpi-value">₹{formatInr(statement.totals.remaining)}</span>
          </div>
          <div className={`las-kpi ${statement.totals.overdue > 0 ? "las-kpi-danger" : ""}`}>
            <span className="las-kpi-label">Overdue Now</span>
            <span className="las-kpi-value">₹{formatInr(statement.totals.overdue)}</span>
          </div>
        </section>

        <section className="las-details">
          {statement.sections.map((section) => (
            <div className="las-detail-card" key={section.title}>
              <h3 className="las-detail-title">{section.title}</h3>
              <dl className="las-dl">
                {section.items.map((item) => (
                  <div className="las-dl-row" key={item.label}>
                    <dt>{item.label}</dt>
                    <dd>{item.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </section>

        <section>
          <div className="las-section-head">
            <h3 className="las-section-title">Transactions</h3>
            <div className="las-legend" aria-label="Row colours">
              <span className="las-legend-item las-legend-carried">Carried forward</span>
              <span className="las-legend-item las-legend-excess">Advance payment</span>
              <span className="las-legend-item las-legend-due">Not yet paid</span>
            </div>
          </div>
          <div className="las-table-wrap">
            <table className="las-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Due Date</th>
                  <th className="las-col-desc">Particulars</th>
                  <th className="las-num">Increased By</th>
                  <th className="las-num">Decreased By</th>
                  <th className="las-num">Interest Part</th>
                  <th className="las-num">Principal Part</th>
                  <th className="las-num">Balance</th>
                </tr>
              </thead>
              <tbody>
                {statement.rows.map((row) => {
                  const groupRow =
                    row.group !== lastGroup ? (
                      <tr className="las-group" key={`group-${row.group}`}>
                        <td colSpan={COLUMN_COUNT}>{GROUP_TITLES[row.group]}</td>
                      </tr>
                    ) : null;
                  lastGroup = row.group;
                  return (
                    <React.Fragment key={row.key}>
                      {groupRow}
                      <tr className={`las-row las-row-${row.kind}`}>
                        <td className="las-date">{row.collectionDate || "-"}</td>
                        <td className="las-date">{row.scheduledDate || "-"}</td>
                        <td className="las-col-desc">
                          <span className="las-desc">{row.description}</span>
                          {row.note && <span className={`las-note las-note-${row.noteTone}`}>{row.note}</span>}
                        </td>
                        <td className="las-num">{money(row.increasedBy)}</td>
                        <td className="las-num">{money(row.decreasedBy)}</td>
                        <td className="las-num">{money(row.interest)}</td>
                        <td className="las-num">{money(row.principal)}</td>
                        <td className="las-num las-balance">{money(row.balance)}</td>
                      </tr>
                    </React.Fragment>
                  );
                })}
                <tr className="las-final">
                  <td colSpan={COLUMN_COUNT - 1}>Final Balance Owed</td>
                  <td className="las-num">₹{formatInr(statement.finalBalance)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {statement.foreclosureSettlement && (
          <section className="las-settlement" aria-label="Foreclosure settlement">
            <div className="las-detail-card">
              <h3 className="las-detail-title">Foreclosure Settlement</h3>
              <dl className="las-dl">
                {statement.foreclosureSettlement.map((item) => (
                  <div className={`las-dl-row ${item.tone ? `las-dl-${item.tone}` : ""}`} key={item.label}>
                    <dt>{item.label}</dt>
                    <dd>{item.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </section>
        )}

        <footer className="las-foot">
          <p className="las-end">End of statement</p>
        </footer>
      </div>
    </article>
  );
};

// Shared screen for the Business Loan and GP Loan Account Statements.
const AccountStatementPage = ({ title, endpoint, product }) => {
  const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 992);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [prospectId, setProspectId] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [viewing, setViewing] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const statement = useMemo(() => {
    if (!result?.memberDetails) return null;
    const summary = generateLoanSummaryForMember(result.memberDetails, "2000-01-01", "2099-12-31");
    // Either page's search can return either product, so the loan's own type decides
    // the rules (BL subtracts charges deducted at disbursement, GP Loan does not).
    const loanProduct = PRODUCT_BY_LOAN_TYPE[result.memberDetails.loanType] || product;
    return buildAccountStatement(summary, { product: loanProduct, branchName: result.branchName });
  }, [result, product]);

  const handleView = async (event) => {
    event.preventDefault();
    const id = prospectId.trim();
    setError("");
    if (!id) {
      setError("Enter a Prospect ID to view the statement.");
      return;
    }
    setViewing(true);
    setResult(null);
    try {
      const response = await axios.get(`${BASE_URL}/api/${endpoint}/${encodeURIComponent(id)}`);
      if (response.data.message) setResult(response.data.message);
      else setError(response.data.error || "No statement found for this Prospect ID.");
    } catch (err) {
      setError(err.response?.data?.error || err.message || "The statement could not be loaded.");
    } finally {
      setViewing(false);
    }
  };

  const handleDownload = async () => {
    if (!statement) return;
    setError("");
    setDownloading(true);
    try {
      await downloadAccountStatementPdf(statement, logo);
    } catch (err) {
      setError(`The PDF could not be created: ${err?.message || err}`);
    } finally {
      setDownloading(false);
    }
  };

  const noSchedule = result?.memberDetails && !statement;

  return (
    <section className="master-layout">
      <div className="master-sidebar-wrapper">
        <Sidebar openSidebarToggle={openSidebarToggle} OpenSidebar={() => setOpenSidebarToggle(!openSidebarToggle)} />
      </div>

      <div className="master-content-wrapper">
        <div className="master-header-wrapper">
          <DashboardHeader OpenSidebar={() => setOpenSidebarToggle(!openSidebarToggle)} />
        </div>

        <div className="master-card-container">
          <div className="master-search-section">
            <h1 className="TableHeaderText w-100 text-start">{title}</h1>

            <button
              className={`filter-toggle-btn ${showMobileFilters ? "active" : ""}`}
              onClick={() => setShowMobileFilters(!showMobileFilters)}
            >
              <span style={{ fontWeight: 600, fontSize: "14px" }}>
                {showMobileFilters ? "Hide Filters" : "Show Filters"}
              </span>
              <MdExpandMore size={22} />
            </button>

            <div className={`filter-container ${!showMobileFilters ? "mobile-hidden" : "mobile-visible"}`}>
              <Form onSubmit={handleView}>
                <Row className="align-items-center">
                  <Col xs={12} lg={4} md={6}>
                    <Form.Control
                      type="text"
                      placeholder="Enter Prospect ID"
                      aria-label="Prospect ID"
                      value={prospectId}
                      onChange={(event) => setProspectId(event.target.value)}
                    />
                  </Col>
                  <Col xs={12} lg="auto" md={6} className="d-flex gap-2 align-items-center las-actions">
                    <button type="submit" className="report-btn" disabled={viewing || downloading}>
                      <MdSearch /> {viewing ? "Loading…" : "View Statement"}
                    </button>
                    {statement && (
                      <button type="button" className="report-btn las-btn-secondary" onClick={handleDownload} disabled={downloading}>
                        <MdPictureAsPdf /> {downloading ? "Preparing PDF…" : "Download PDF"}
                      </button>
                    )}
                  </Col>
                </Row>
              </Form>
            </div>
            {(error || noSchedule) && (
              <div className="las-alert w-100" role="alert">
                {error || "This loan's repayment schedule could not be calculated (missing loan amount, rate, tenure or EMI)."}
              </div>
            )}
          </div>

          <div className="master-list-section">
            {statement ? (
              <StatementDocument statement={statement} />
            ) : (
              !error && !viewing && !noSchedule && (
                <div className="las-empty">Enter a Prospect ID and choose View Statement.</div>
              )
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AccountStatementPage;
